import React, { useState, useRef, useEffect, useLayoutEffect } from 'react';
import { List } from 'oolib';
import { Lightbox, LightboxContent } from '@/components/UI/lightbox';
import { SearchActionsToolbar } from '@/components/UI/DrilldownList';

/**
 * Filters options based on search term.
 * Handles both flat and nested (grouped) option structures.
 */
const filterOptions = (data, searchTerm) => {
  if (!searchTerm || searchTerm.trim() === '') return data;
  const term = searchTerm.toLowerCase().trim();

  return data.reduce((acc, item) => {
    if (item.options && Array.isArray(item.options)) {
      const outerDisplay = (item.display || '').toLowerCase();
      const outerDesc = (item.desc || '').toLowerCase();
      const outerMatches = outerDisplay.includes(term) || outerDesc.includes(term);

      const filteredOptions = item.options.filter(option => {
        const innerDisplay = (option.display || '').toLowerCase();
        const innerDesc = (option.desc || '').toLowerCase();
        return innerDisplay.includes(term) || innerDesc.includes(term);
      });

      if (outerMatches || filteredOptions.length > 0) {
        acc.push({
          ...item,
          options: outerMatches ? item.options : filteredOptions
        });
      }
    } else {
      const display = (item.display || '').toLowerCase();
      const desc = (item.desc || '').toLowerCase();
      if (display.includes(term) || desc.includes(term)) {
        acc.push(item);
      }
    }
    return acc;
  }, []);
};

/**
 * SearchableLightbox
 *
 * A reusable lightbox with search + List, anchored to a ref element.
 * When `renderContent` is provided, replaces the default search + list
 * with custom content (e.g., tabbed UI) while keeping the positioned shell.
 *
 * Hosted on the shared `UI/lightbox` primitives (same shell as the cascade
 * picker): a single floating panel positioned to the RIGHT of the anchor
 * (`right-start`), matching where the previous DynamicOptionsLightboxShell
 * placed it (`anchor.right + offset`). `multilayer={false}` keeps it a plain
 * flat panel — no nested cascade columns.
 *
 * @param {Object}   anchorRef         — ref to the anchor element
 * @param {Function} onClose           — () => void
 * @param {Array}    options           — List options (flat or grouped)
 * @param {*}        value             — Current selected value(s)
 * @param {Function} onChange          — List onChange handler
 * @param {Boolean}  grouped           — Whether options are grouped
 * @param {Boolean}  isMulti           — Multi-select mode
 * @param {Number}   width             — Lightbox width (default 300)
 * @param {String}   searchPlaceholder — Placeholder text (default "Search")
 * @param {Function} renderContent     — ({ onClose }) => JSX — replaces default content
 * @param {Boolean}  hideSearchHeader     — Hide the entire header (search + close + divider)
 */
export const SearchableLightbox = ({
  anchorRef,
  onClose,
  options = [],
  value,
  onChange,
  grouped = false,
  isMulti = false,
  width = 300,
  searchPlaceholder = 'Search',
  renderContent,
  hideSearchHeader = false,
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const ref_header = useRef(null);
  const [headerTop, setHeaderTop] = useState(0);

  // The new Lightbox positions against an actual element (anchorEl), not a ref.
  // Seed from anchorRef.current at mount (the anchor is already rendered by the
  // time this conditionally-mounted picker appears, so there's no (0,0) flash),
  // and resync if the ref target changes.
  const [anchorEl, setAnchorEl] = useState(() => anchorRef?.current ?? null);
  useLayoutEffect(() => {
    setAnchorEl(anchorRef?.current ?? null);
  }, [anchorRef]);

  useEffect(() => {
    const h = ref_header.current?.getBoundingClientRect()?.height;
    if (h) setHeaderTop(h + 'px');
  }, []);

  const filteredOptions = filterOptions(options, searchTerm);

  return (
    <Lightbox
      open
      onOpenChange={(v) => { if (!v) onClose?.(); }}
      anchorEl={anchorEl}
      placement="right-start"
      multilayer={false}
      enabled
    >
      {/* trapFocus off so the anchor keeps focus and the search input is
          immediately usable, matching the cascade picker's root panel. */}
      <LightboxContent trapFocus={false} style={{ zIndex: 100000, width }}>
        {renderContent ? (
          renderContent({ onClose })
        ) : (
          <div>
            {/* hideSearchHeader hides the whole header (search + close + divider),
                for pickers with a tiny fixed option set where search is noise.
                Plain measuring wrapper otherwise: the toolbar owns its own
                sticky positioning; we only read its height to offset grouped
                List headers via groupHeaderStickyTop. */}
            {!hideSearchHeader && (
              <div ref={ref_header}>
                <SearchActionsToolbar
                  search={{ value: searchTerm, onChange: setSearchTerm }}
                  onClose={onClose}
                />
              </div>
            )}
            <List
              groupHeaderStickyTop={headerTop}
              disableShadow
              {...(grouped ? { grouped: true } : {})}
              {...(isMulti ? { isMulti: true } : {})}
              options={filteredOptions}
              value={value}
              onChange={onChange}
            />
          </div>
        )}
      </LightboxContent>
    </Lightbox>
  );
};
